import { FC, useEffect, useRef, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { GroupBase, OptionsOrGroups } from "react-select";
import { format, parse } from "date-fns";
import HubInputField from "../../inputType";
import AddCasePhoneNumber from "../addCasePhoneNumber";

import { AddCasePatientInformaitonProps } from "./types";
import { PATIENT_GENDER, PATIENT_TIME_TO_CONTACT, PATIENT_TITLE } from "src/constants/cases";
import { PatientPhoneNumber } from "src/pages/addCase/types";
import DatePicker from "react-datepicker";
import ReactInputMask from "react-input-mask";
import { isOlderthan18, kgToLb, lbToKg } from "src/helpers/addCase";
import ConfirmPhoneModal from "src/components/modals/confirmPhoneModal";

const preferredLanguage = [
  {
    "id": "English",
    "name": "English",
  },
  {
    "id": "Spanish",
    "name": "Spanish",
  },
];





const AddCasePatientInformation: FC<AddCasePatientInformaitonProps> = ({ control, watch, getValues, setValue, trigger, setFocus }): JSX.Element => {
  const [patientWeightUnit, updatePatientWeightUnit] = useState<string>("lb")
  const [showModal, setShowModal] = useState(false);
  const [currentNumber, setCurrentNumber] = useState("");

  const patientPhoneListing = watch("patient_attributes.phone_numbers") ?? [];
  const dob = watch("patient_attributes.dob")
  const patientCaregiverPhoneNumber = watch("patient_attributes.caregiver_phone_number")

  const caregiverInputRef = useRef<HTMLInputElement>(null);
  const relationRef = useRef<HTMLInputElement>(null);


  const handleBlur = (value: string) => {
    if (dob && !isOlderthan18(dob)) {
      if (value.trim().length === 10) {
        setCurrentNumber(value);
        setShowModal(true);
      }
    }

  };

  const removePhoneNumber = (index: number): void => {
    const phoneNumberlisting: PatientPhoneNumber[] = [...(getValues("patient_attributes.phone_numbers") ?? [])];
    phoneNumberlisting.splice(index, 1);
    setValue("patient_attributes.phone_numbers", phoneNumberlisting);
  };

  const addNewPhoneNumber = (): void => {
    const phoneNumbers: PatientPhoneNumber[] = [...(getValues("patient_attributes.phone_numbers") ?? [])];
    const newPhoneNumber = {
      phone_type: "cell",
      id: null,
    } as PatientPhoneNumber;
    phoneNumbers.push(newPhoneNumber);
    setValue("patient_attributes.phone_numbers", phoneNumbers);
  };

  useEffect(() => {
    if (dob) {
      if (!isOlderthan18(dob)) {
        trigger && trigger("patient_attributes.caregiver_phone_number")
        trigger && trigger("patient_attributes.caregiver_name")
        setValue(
          "patient_attributes.caregiver_phone_number",
          (patientPhoneListing && patientPhoneListing.length > 0 && patientPhoneListing[0].number) || ""
        )
      } else if (isOlderthan18(dob)) {
        setValue("patient_attributes.weight", "0")
        trigger && trigger("patient_attributes.caregiver_phone_number")
        trigger && trigger("patient_attributes.caregiver_name")
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dob])

  useEffect(() => {
    if (dob && !isOlderthan18(dob)) {
      setValue(
        "patient_attributes.phone_numbers.0.number",
        getValues("patient_attributes.caregiver_phone_number") || ""
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientCaregiverPhoneNumber])


  useEffect(() => {
    if (dob && !isOlderthan18(dob)) {
      const phoneNumbers: PatientPhoneNumber[] = getValues("patient_attributes.phone_numbers") || [];
      setValue(
        "patient_attributes.caregiver_phone_number",
        (phoneNumbers && phoneNumbers.length > 0 && phoneNumbers[0].number) || ""
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientPhoneListing?.[0]?.number])

  return (
    <>
      <Controller
        name="patient_attributes.title"
        control={control}
        defaultValue=""
        render={({ field, fieldState: { error } }) => (
          <HubInputField
            inputId="patient_attributes.title"
            inputPlaceholder="Select a Title..."
            inputLabel="Title"
            isRequired={false}
            isRowBased={true}
            field={field}
            error={error}
            isDropdown={true}
            isSearchable={false}
            isTypeAhead
            dropdownData={PATIENT_TITLE as unknown as OptionsOrGroups<string, GroupBase<string>>}
          />
        )}
      />

      <div className="row">
        <label className="form-label col-lg-2 col-md-4 col-12">Patient Name *</label>
        <div className="col-lg-10 col-md-8 col-12">
          <div className="row">
            <div className="col-lg-4 col-md-4 col-12">
              <Controller
                name="patient_attributes.firstname"
                control={control}
                defaultValue=""
                render={({ field, fieldState: { error } }) => (
                  <HubInputField
                    inputId="patient_attributes.firstname"
                    isRowBased={true}
                    isRequired={false}
                    field={field}
                    inputPlaceholder="First Name *"
                    error={error}
                  />
                )}
              />
            </div>
            <div className="col-lg-4 col-md-4 col-12">
              <Controller
                name="patient_attributes.middlename"
                control={control}
                defaultValue=""
                render={({ field, fieldState: { error } }) => (
                  <HubInputField
                    isRowBased={true}
                    isRequired={false}
                    field={field}
                    inputId="patient_attributes.middlename"
                    inputPlaceholder="Middle Name"
                    error={error}
                  />
                )}
              />
            </div>
            <div className="col-lg-4 col-md-4 col-12">
              <Controller
                name="patient_attributes.lastname"
                control={control}
                defaultValue=""
                render={({ field, fieldState: { error } }) => (
                  <HubInputField
                    isRowBased={true}
                    isRequired={false}
                    field={field}
                    inputId="patient_attributes.lastname"
                    inputPlaceholder="Last Name *"
                    error={error}
                  />
                )}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12 col-lg-4">
          <div className="row">
            <label className="form-label col-md-4 col-lg-6 col-12">Gender *</label>
            <div className="col-md-8 col-lg-6 col-12">
              <Controller
                name="patient_attributes.gender"
                control={control}
                defaultValue=""
                render={({ field, fieldState: { error } }) => (
                  <HubInputField
                    inputId="patient_attributes.gender"
                    isRequired={true}
                    isRowBased={true}
                    field={field}
                    error={error}
                    isDropdown={true}
                    isSearchable={false}
                    isTypeAhead
                    dropdownData={PATIENT_GENDER as unknown as OptionsOrGroups<string, GroupBase<string>>}
                  />
                )}
              />
            </div>
          </div>
        </div>
        <div className="col-12 col-lg-4 mb-2">
          <Controller
            name="patient_attributes.dob"
            control={control}
            defaultValue=""
            render={({ field, fieldState: { error } }) => (
              <div className="row align-items-center">
                <label htmlFor="patient_attributes.dob" className="col-12 col-md-4 form-label text-end mb-0">
                  DoB *
                </label>
                <div className="col-12 col-md-8 position-relative">
                  <DatePicker
                    selected={
                      field.value
                        ? parse(field.value, "yyyy-MM-dd", new Date()) // Parse default yyyy-MM-dd to a Date
                        : null
                    }
                    onChange={(date) => {
                      field.onChange(date ? format(date, "yyyy-MM-dd") : ""); // Convert back to yyyy-MM-dd for consistency
                    }}
                    dateFormat="MM/dd/yyyy" // User-facing format
                    className={`form-control ${error?.message ? "is-invalid" : ""}`}
                    placeholderText="mm/dd/yyyy"
                    showMonthDropdown
                    showYearDropdown
                    customInput={
                      <ReactInputMask mask="99/99/9999">
                        {(inputProps: {}) => <input {...inputProps} />}
                      </ReactInputMask>
                    }
                    maxDate={new Date()} // Restrict future dates
                    wrapperClassName="w-100"
                  />
                  <i
                    className="bi bi-calendar position-absolute"
                    style={{
                      right: error?.message ? "50px" : "25px", // Adjust position based on error
                      top: "18px",
                      transform: "translateY(-50%)",
                      pointerEvents: "none",
                    }}
                  ></i>
                  {error && error.message ? (
                    <div
                      dangerouslySetInnerHTML={{ __html: error.message }}
                      className="text-danger text-small mt-2"
                    ></div>
                  ) : null}
                </div>
              </div>
            )}
          />
        </div>
        <div className="col-12 col-lg-4 mb-2">
          {dob && !isOlderthan18(dob) ? (
            <Controller
              name="patient_attributes.weight"
              control={control}
              defaultValue="0"
              render={({ field, fieldState: { error } }) => (
                <div className="row">
                  <label htmlFor="patient_attributes.weight" className="form-label col-lg-6 col-md-4 col-12">
                    Patient Weight 
                  </label>
                  <div className="col-lg-6 col-md-8 col-12">
                    <div className={`input-group ${error && error.message ? "is-invalid" : ""}`}>
                      <input
                        type="text" className="form-control" aria-label="Text input with dropdown button"
                        id="patient_attributes.weight"
                        {...field}
                        value={field.value ? field.value.replace(/(kg|lb)$/, "") : ""}
                        onChange={(e) => field.onChange(`${e.target.value}${patientWeightUnit || ""}`)}
                      />
                      <button
                        className="btn btn-outline-secondary dropdown-toggle rounded-end-3" type="button"
                        data-bs-toggle="dropdown" aria-expanded="false"
                      >
                        {patientWeightUnit.length > 0 ? patientWeightUnit : "Unit"}
                      </button>
                      <ul className="dropdown-menu dropdown-menu-end">
                        <li
                          onClick={() => {
                            if (field.value && field.value.length > 0) {
                              updatePatientWeightUnit("kg")
                              const val = field.value.replace(/(kg|lb)$/, "");
                              if (!isNaN(Number(val))) {
                                field.onChange(`${lbToKg(Number(val))}kg`)
                              }
                            }
                          }}
                          className="dropdown-item"
                        >kg</li>
                        <li
                          onClick={() => {
                            if (field.value && field.value.length > 0) {
                              updatePatientWeightUnit("lb")
                              const val = field.value.replace(/(kg|lb)$/, "");
                              if (!isNaN(Number(val))) {
                                field.onChange(`${kgToLb(Number(val))}lb`)
                              }
                              // field.onChange(`${field.value.replace(/(kg|lb)$/, "")}lb`)
                            }
                          }}
                          className="dropdown-item"
                        >lb</li>
                      </ul>
                    </div>
                    {error && error.message ? (
                      <div dangerouslySetInnerHTML={{ __html: error.message }} className="text-danger text-small mt-2 "></div>
                    ) : null}
                  </div>
                </div>
              )}
            />
          ) : null}
        </div>
      </div>

      <div className="row my-3">
        <div className="col-lg-6 col-12">
          <Controller
            name="patient_attributes.language"
            control={control}
            defaultValue=""
            render={({ field, fieldState: { error } }) => (
              <HubInputField
                inputId="patient_attributes.language"
                isRequired={false}
                isRowBased={true}
                isRowInsideAnother={true}
                field={field}
                error={error}
                isDropdown={true}
                isSearchable={false}
                inputPlaceholder="Select Preferred Language..."
                inputLabel="Preferred Language"
                isTypeAhead
                dropdownData={
                  (preferredLanguage ?
                    preferredLanguage.map((item) => ({ id: item.id, name: item.name })).map(
                      (procedure) => ({ value: procedure.id, label: procedure.name })
                    ) : []
                  ) as unknown as OptionsOrGroups<string, GroupBase<string>>
                }
              />
            )}
          />
        </div>
        <div className="col-lg-6 col-12">
          <Controller
            name="patient_attributes.best_time_to_contact"
            control={control}
            defaultValue=""
            render={({ field, fieldState: { error } }) => (
              <div className="row">
                <label className="form-label col-lg-4 col-md-4 col-12">Best Time to Contact</label>
                <div className="col-lg-8 col-md-8 col-12">
                  <HubInputField
                    inputId="patient_attributes.best_time_to_contact"
                    isRequired={false}
                    isRowBased={true}
                    field={field}
                    error={error}
                    inputPlaceholder="Select Best Time to Contact..."
                    isDropdown={true}
                    isSearchable={false}
                    isTypeAhead
                    dropdownData={PATIENT_TIME_TO_CONTACT as unknown as OptionsOrGroups<string, GroupBase<string>>}
                  />
                </div>
              </div>
            )}
          />
        </div>
      </div>

      {patientPhoneListing && patientPhoneListing.map((_d, index) => (
        <AddCasePhoneNumber key={index} control={control} phoneNumberIndex={index} removePhoneNumber={removePhoneNumber} />
      ))}
      <div className="text-end mb-3 mt-2">
        <button type="button" className="btn btn-success" onClick={addNewPhoneNumber}>
          <i className="bi bi-plus-lg"></i> Add Phone Number
        </button>
      </div>

      {showModal && (
        <ConfirmPhoneModal
          onClose={() => {
            setShowModal(false);
            caregiverInputRef.current?.focus();
          }}
          onConfirm={() => {
            setShowModal(false);
            relationRef.current?.focus();
          }}
          phoneNumber={currentNumber}
        />
      )}
        <div className="row">
            <div className="col-12 col-lg-4">
                <div className="row">
                    <label className="form-label col-md-4 col-lg-6 col-12">{dob && !isOlderthan18(dob) ? "Parent/Guardian Name*" : "Caregiver Name"} </label>
                    <div className="col-md-8 col-lg-6 col-12">
                        <Controller
                            name="patient_attributes.caregiver_name"
                            control={control}
                            defaultValue=""
                            render={({ field, fieldState: { error } }) => (
                                <HubInputField
                                    inputId="patient_attributes.caregiver_name"
                                    isRowBased={true}
                                    isRequired={false}
                                    field={field}
                                    inputPlaceholder="First and Last Name"
                                    error={error}
                                />
                            )}
                        />
                    </div>
                </div>
            </div>

            <div className="col-12 col-lg-4">
                <div className={"row"}>
                    <label className="form-label col-md-4 col-lg-6 col-12">{dob && !isOlderthan18(dob) ? "Parent/Guardian Phone Number*" : "Caregiver Phone Number"} </label>
                    <div className="col-md-8 col-lg-6 col-12">
                        <Controller
                            name={`patient_attributes.caregiver_phone_number`}
                            control={control}
                            defaultValue=""
                            render={({ field, fieldState: { error } }) => (
                                <div className="row mb-3">
                                    <div className="col-12">
                                        <ReactInputMask
                                            {...field}
                                            mask="(999) 999-9999"
                                            onChange={(e) => field.onChange(e.target.value.replace(/\D/g, ''))}
                                            onBlur={() => { !isOlderthan18(dob) && handleBlur(field.value || "") }}
                                        >
                                            {(inputProps: {}) =>
                                                <input type="text"
                                                       id={`patient_attributes.caregiver_phone_number`}
                                                       className="form-control"
                                                       ref={caregiverInputRef}
                                                       placeholder="(555) 555-5555" {...inputProps} />}
                                        </ReactInputMask>

                                        {error && error.message ? (
                                            <div dangerouslySetInnerHTML={{ __html: error.message }}
                                                 className="text-danger text-small mt-2"></div>
                                        ) : null}
                                    </div>
                                </div>
                            )}
                        />
                    </div>
                </div>
            </div>

            <div className="col-12 col-lg-4">
                <div className={"row"}>
                    <label className="form-label col-md-4 col-lg-6 col-12">Relation to Patient</label>
                    <div className="col-md-8 col-lg-6 col-12">
                        <Controller
                            name="patient_attributes.relationship_to_patient"
                            control={control}
                            defaultValue=""
                            render={({ field, fieldState: { error } }) => (
                                <HubInputField
                                    inputId="patient_attributes.relationship_to_patient"
                                    inputRef={relationRef}
                                    isRowBased={true}
                                    isRequired={false}
                                    field={field}
                                    error={error}
                                />
                            )}
                        />
                    </div>
                </div>
            </div>
        </div>
    </>
  );
};

export default AddCasePatientInformation;
